<template>
  <div id="player-details-view">
    <v-carousel-transition>
      <v-container
        v-if="player.loading"
        class="d-flex justify-center align-center my-16"
      >
        <v-progress-circular
          indeterminate
          size="75"
        />
      </v-container>
      <v-row
        v-else-if="player.data"
        no-gutters
        justify="center"
      >
        <v-col
          xl="11"
          md="12"
        >
          <player-details-card
            :player="player.data"
            :stats="player.stats"
            @edit:hr-profile="hrProfile.dialog = true"
          />

          <div
            v-if="hasPlayerTimezones && player.data && player.data.settings && player.data.settings.timezone"
            class="caption grey--text text--lighten-1 mt-4 text-right"
          >
            {{ $t('player_profile.timezone_label') }}: <strong>{{ timezones.find(tz => tz.tzCode === player.data.settings.timezone).name }}</strong>
          </div>

          <v-tabs
            v-model="view"
            background-color="transparent"
            class="mt-4"
          >
            <v-tab
              v-if="hasItlModule"
              class="text-none"
              href="#load"
            >
              {{ $t('table_headers.main_load.training_load') }}
            </v-tab>
            <v-tab
              v-if="$can('view-performance')"
              class="text-none"
              href="#performance"
            >
              {{ $t('individual_analysis.performance') }}
            </v-tab>
            <v-tab
              v-if="user.organisation_settings.has_training_builder_module && $can('view-training-protocols')"
              class="text-none"
              href="#trainings"
            >
              {{ $t('individual_analysis.training_exercises') }}
            </v-tab>
            <v-tab
              v-if="$can('view-performance')"
              class="text-none"
              href="#match"
            >
              {{ $t('individual_analysis.match') }}
            </v-tab>
            <v-tab
              v-if="hasSmartAssistantModule"
              class="text-none"
              href="#smart-assistant-alerts"
            >
              <v-badge
                v-if="player.data.smart_assistant.length"
                color="primary"
                dot
              >
                {{ $t("individual_analysis.smart_assistant") }}
              </v-badge>
              <div v-else>
                {{ $t("individual_analysis.smart_assistant") }}
              </div>
            </v-tab>
            <v-tab
              v-if="$can('view-diagnostics')"
              class="text-none"
              href="#diagnostics"
            >
              {{ $t('home.diagnostics') }}
            </v-tab>
            <v-tab
              v-if="hasMedicalModule && $can('view-medical')"
              class="text-none"
              href="#medical"
            >
              {{ $t('home.medical') }}
            </v-tab>
            <v-tab
              v-if="$can('view-player-media')"
              class="text-none"
              href="#media"
            >
              {{ $t('individual_analysis.media') }}
            </v-tab>
            <v-tab
              v-if="$can('view-player-notes')"
              class="text-none"
              href="#notes"
            >
              {{ $t('text_input.notes.title') }}
            </v-tab>
            <v-tab
              v-if="$can('view-event-attendance')"
              class="text-none"
              href="#attendance"
            >
              {{ $t('individual_analysis.event_attendance') }}
            </v-tab>
            <!--            <v-tab class="text-none">-->
            <!--              Performance-->
            <!--            </v-tab>-->
          </v-tabs>

          <div v-if="view === 'load'">
            <v-card>
              <v-card-title class="d-flex justify-space-between">
                <div class="subtitle-2">
                  {{ $t('table_title.load_management') }}
                </div>
              </v-card-title>
              <v-card-text class="pa-0">
                <player-training-load-table
                  :stats="playerStats.data"
                  :loading="playerStats.loading"
                  :pagination="playerStats.pagination"
                  :max-values="playerStats.maxValues"
                  :show-menstrual-period="showMenstrualPeriod"
                  :show-wellness-data="showWellnessData"
                  :player="player.data"
                />
              </v-card-text>
            </v-card>

            <section class="my-5">
              <line-chart
                :raw-data="playerStats.data"
                :chart-value-keys="[ { key: 'DL', type: 'bar', label: $t('chart_legends.daily_load') }, { key: 'AL', type: 'bar', label: $t('chart_legends.acute_load') }, { key: 'CL', type: 'bar', label: $t('chart_legends.chronic_load') }, ...(showWellnessData ? [{ key: 'player_wellness', type: 'line', label: $t('card_title.player_wellness') }] : []), { key: 'FRES', type: 'line', label: $t('table_headers.main_load.freshness') } ]"
                x-value="date"
                y-value-prefix="stats."
                :yaxis="[ { title: { text: $t('chart_legends.load') }, seriesName: 'DL' }, { title: { text: '' }, labels: { show:false }, seriesName: 'AL' }, { title: { text: '' }, labels: { show:false }, seriesName: 'CL' }, ...(showWellnessData ? [{ title: { text: $t('table_headers.weekly_load.wellness') }, opposite: true, max: 100 }] : []), { title: { text: $t('table_headers.main_load.freshness') }, opposite: true } ]"
                :plot-options="{
                  bar: {
                    borderRadius: 5,
                    columnWidth: '30%'
                  }
                }"
                :annotations="{ xaxis: annotateMenstrual ? xAxisMenstrualAnnotations : [] }"
              >
                <template v-slot:header>
                  <v-card-title class="pa-6">
                    <v-row no-gutters>
                      <v-col>
                        <div class="subtitle-2 grey--text text--grey-100">
                          {{ $t("card_title.player_daily_acute_chronic") }}{{ showWellnessData ? ',' + $t("card_title.player_wellness") : '' }} {{ $t("card_title.and_freshness") }}
                        </div>
                      </v-col>
                      <v-col
                        v-if="showMenstrualPeriod && xAxisMenstrualAnnotations.length > 0"
                        cols="auto"
                      >
                        <v-btn
                          small
                          :color="!annotateMenstrual ? 'grey darken-2' : 'primary'"
                          :outlined="!annotateMenstrual"
                          class="text-none"
                          @click="annotateMenstrual = !annotateMenstrual"
                        >
                          {{ $t("button.show_menstrual_phases") }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-title>
                </template>
              </line-chart>
            </section>

            <v-card class="mb-5 rounded-lg">
              <v-card-title class="pa-6">
                <div class="subtitle-2 grey--text text--grey-100">
                  {{ $t('card_title.player_pain_trend') }}
                </div>
              </v-card-title>
              <v-card-text class="px-12 py-10">
                <v-row v-if="painTrendData.length > 2">
                  <v-col
                    v-for="stat in painTrendData"
                    :key="stat.id"
                    class="text-center"
                  >
                    <div>
                      <pain-avatar-group
                        :player-stats="stat.stats"
                        :gender="player.data.gender"
                        :width="150"
                        :height="250"
                      />
                    </div>
                    <div class="grey--text text--darken-2 text-body-2 pt-4 text-center">
                      <span v-if="$dayjs(stat.date).isSame($dayjs(), 'day')">
                        {{ $t('card.team_average_pain.today') }}
                      </span>
                      <span v-else-if="$dayjs(stat.date).subtract($dayjs().utcOffset(), 'minute').diff($dayjs(), 'day') === -1">
                        {{ $t('card.team_average_pain.yesterday') }}
                      </span>
                      <span v-else>
                        {{ $dayjs(stat.date).format('dddd, MMMM D') }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
                <div
                  v-else
                  class="grey--text pb-10 text-center"
                >
                  Player pain trend not yet available
                </div>
              </v-card-text>
            </v-card>

            <v-row class="mb-10">
              <v-col>
                <section class="fill-height">
                  <line-chart
                    class="fill-height"
                    :raw-data="playerStats.data"
                    :chart-value-keys="[ 'ACWR' ]"
                    x-value="date"
                    y-value-prefix="stats."
                    :yaxis="[ { title: { text: $t('table_headers.player_overview.acwr') }, min: 0, max: 3}]"
                    :annotations="{
                      position: 'back',
                      yaxis: [ { y: 0.8, y2: 1.3, opacity: 0.5, borderColor: '#86CC8E', fillColor: '#86CC8E' }, { y: 0.6, y2: 0.8, opacity: 0.5, borderColor: '#F4A641', fillColor: '#F4A641' }, { y: 1.3, y2: 1.5, opacity: 0.5, borderColor: '#F4A641', fillColor: '#F4A641' } ]
                    }"
                  >
                    <template v-slot:header>
                      <v-card-title class="pa-6">
                        <div class="subtitle-2 grey--text text--grey-100">
                          {{ $t('card_title.player_acwr_trend') }}
                        </div>
                      </v-card-title>
                    </template>
                  </line-chart>
                </section>
              </v-col>
              <v-col v-if="showWellnessData">
                <section class="fill-height">
                  <radar-chart
                    :series="[ { name: $t('player.label.title'), data: [ playerLatestStats.FAT_perc || 0, playerLatestStats.GMS_perc || 0, playerLatestStats.MOO_perc || 0, playerLatestStats.STL_perc || 0 ] }, { name: $t('dropdown.team.title'), data: [ teamLatestStats.FAT_perc || 0, teamLatestStats.GMS_perc || 0, teamLatestStats.MOO_perc || 0, teamLatestStats.STL_perc || 0 ] } ]"
                    :xaxis="{ categories: [$t('table_headers.player_overview.fatigue'), $t('chart_legends.muscle_soreness'), $t('table_headers.player_overview.mood'), $t('table_headers.main_load.stress')] }"
                    class="fill-height"
                    :yaxis="{ min: 0, max: 100, tickAmount: 5, labels: { offsetX: -13 }}"
                  >
                    <template v-slot:header>
                      <v-card-title class="pa-6">
                        <div class="subtitle-2 grey--text text--grey-100">
                          {{ $t('card_title.team_fatigue_soreness_mood_stress') }}
                        </div>
                      </v-card-title>
                    </template>
                  </radar-chart>
                </section>
              </v-col>
              <v-col v-if="showWellnessData">
                <section class="fill-height">
                  <line-chart
                    class="fill-height"
                    :raw-data="playerStats.data"
                    :chart-value-keys="[ { key: 'SLQ', label: $t('chart_legends.sleep_quality'), type: 'line' }, { key: 'SLD', label: $t('chart_legends.sleep_duration'), type: 'line' }, { key: 'DL', label: $t('chart_legends.daily_load'), type: 'bar' } ]"
                    x-value="date"
                    y-value-prefix="stats."
                    :yaxis="[ { title: { text: 'SLQ' }}, { title: { text: 'SLD' }, opposite: true}, { title: { text: '' }, labels: { show:false }} ]"
                  >
                    <template v-slot:header>
                      <v-card-title class="pa-6">
                        <div class="subtitle-2 grey--text text--grey-100">
                          {{ $t('card_title.player_sleep_quality') }}
                        </div>
                      </v-card-title>
                    </template>
                  </line-chart>
                </section>
              </v-col>
            </v-row>
          </div>
          <div v-else-if="view === 'performance'">
            <player-performance-table
              :stats="playerStats.data"
              :loading="playerStats.loading || variables.loading"
              :pagination="playerStats.pagination"
              :max-values="playerStats.maxValues"
              :player="player.data"
              :x-axis-menstrual-annotations="xAxisMenstrualAnnotations"
              :show-menstrual-period="showMenstrualPeriod"
              :table-headers="tableHeadersGPS"
              :variables="variables.data"
              @open:customize="customizeModal = true"
            />
          </div>
          <div v-else-if="view === 'attendance'">
            <player-event-attendance-view
              v-if="player.data"
              :player="player.data"
              :player-stats="playerStats.data[0]"
            />
          </div>
          <div v-else-if="view === 'media'">
            <player-media-view
              v-if="player.data"
              :player="player.data"
            />
          </div>
          <div v-else-if="view === 'notes'">
            <player-notes-view
              v-if="player.data"
              :player="player.data"
              @update:player="fetchPlayerDetails()"
            />
          </div>
          <div v-else-if="view === 'match'">
            <player-match-view
              :player="player.data"
              :table-headers="tableHeadersGPS"
              :variables="variables.data"
              @open:customize="customizeModal = true"
            />
          </div>
          <div v-else-if="view === 'smart-assistant-alerts'">
            <player-smart-assistant-alerts-view
              :player="player.data"
            />
          </div>
          <div v-else-if="view === 'diagnostics'">
            <player-diagnostic-view
              v-if="player.data"
              :player="player.data"
            />
          </div>
          <div v-else-if="view === 'medical'">
            <player-medical-view
              v-if="player.data"
              :player="player.data"
            />
          </div>
          <div v-else-if="view === 'trainings'">
            <player-trainings-view
              v-if="player.data"
              :player="player.data"
              :date-from-to="dateRange"
            />
          </div>
        </v-col>
      </v-row>
      <div v-else>
        {{ $t('alert.something_went_wrong') }}
      </div>
    </v-carousel-transition>

    <performance-profile-dialog
      v-if="player.data"
      :visible.sync="hrProfile.dialog"
      :data="player.data"
      type="player"
      @save="updatePlayerPerformanceProfile"
    />

    <performance-coach-customize
      :visible.sync="customizeModal"
      :dashboard="dashboard"
      view="GPS"
      @save="fetchVariables"
    />
  </div>
</template>

<script>
import { CoachApiService, PlayerApiService, TeamApiService } from '@/data/services'
import PlayerDetailsCard from '@/components/players/PlayerDetailsCard'
import LineChart from '@/components/charts/LineChart'
import PlayerTrainingLoadTable from '@/components/players/PlayerTrainingLoadTable'
import { processMaxValues } from '@/utils/helpers'
import RadarChart from '@/components/charts/RadarChart'
import dayjs from 'dayjs'
import PlayerEventAttendanceView from '@/views/player/PlayerEventAttendanceView.vue'
import { MenstrualPhase, MenstrualPhases } from '@/data/models'
import PainAvatarGroup from '@/components/helpers/PainAvatarGroup.vue'
import PlayerPerformanceTable from '@/components/players/PlayersPerformanceTable.vue'
import PlayerMediaView from '@/views/player/PlayerMediaView.vue'
import PlayerNotesView from '@/views/player/PlayerNotesView.vue'
import PlayerMatchView from '@/views/player/PlayerMatchView.vue'
import { cloneDeep, orderBy } from 'lodash'
import PlayerDiagnosticView from '@/views/player/PlayerDiagnosticView.vue'
import PlayerTrainingsView from '@/views/player/PlayerTrainingsView.vue'
import PlayerMedicalView from '@/views/player/PlayerMedicalView.vue'
import PlayerSmartAssistantAlertsView from '@/views/player/PlayerSmartAssistantAlertsView.vue'
import PerformanceProfileDialog from '@/components/players/PerformanceProfileDialog.vue'
import PerformanceCoachCustomize from '@/components/performance/PerfomanceCoachCustomize.vue'
import { DashboardType } from '@/data/models/Dashboard'
import timezones from 'timezones-list'

export default {
  name: 'PlayerDetailsView',
  // eslint-disable-next-line vue/no-unused-components
  components: { PerformanceCoachCustomize, PlayerSmartAssistantAlertsView, PerformanceProfileDialog, PlayerMedicalView, PlayerTrainingsView, PlayerMatchView, PlayerDiagnosticView, PlayerNotesView, PlayerMediaView, PlayerPerformanceTable, PainAvatarGroup, PlayerEventAttendanceView, RadarChart, PlayerTrainingLoadTable, LineChart, PlayerDetailsCard },
  data: () => ({
    view: 'load',
    player: {
      data: null,
      loading: false
    },
    playerStats: {
      data: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 14
      },
      maxValues: {},
      loading: false
    },
    teamStats: {
      data: [],
      loading: false
    },
    hrProfile: {
      dialog: false
    },
    variables: {
      data: null,
      loading: false
    },
    customizeModal: false,
    dashboard: DashboardType.INDIVIDUAL_PERFORMANCE,
    xAxisMenstrualAnnotations: [],
    annotateMenstrual: false,
    dateRange: null
  }),
  computed: {
    playerLatestStats() {
      if (this.playerStats.data.length === 0) return {}

      return this.playerStats.data[0].stats
    },
    teamLatestStats() {
      if (this.teamStats.data.length === 0) return {}

      return this.teamStats.data[0].stats
    },
    showMenstrualPeriod() {
      if (this.player.data && this.player.data.gender === 'female') return true

      return false
    },
    painTrendData() {
      return this.playerStats.data.slice(0, 5).reverse()
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    showWellnessData() {
      return !this.user.organisation_settings.has_short_wellness_feedback
    },
    hasMedicalModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_medical_module
    },
    hasSmartAssistantModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_smart_assistant_module
    },
    tableHeadersGPS() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.GPS.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module || false
    },
    hasPlayerTimezones() {
      return this.user.organisation_settings.has_player_timezones || false
    }
  },
  watch: {
    'playerStats.pagination.current_page': function (newVal, oldVal) {
      this.fetchPlayerStats()
    },
    '$route.query.view': function (newVal, oldVal) {
      if (newVal === 'trainings') {
        this.view = 'trainings'
      } else if (newVal === 'trainingLoad') {
        this.view = 'load'
      }

      if (this.$route.query.from) {
        this.dateRange = {
          from: this.$route.query.from,
          to: this.$route.query.to
        }

        this.$router.replace({ query: {} })
      }

      this.fetchPlayerDetails()
      this.fetchPlayerStats()
    }
  },
  created() {
    this.timezones = timezones

    if (this.$route.query.view === 'trainings') {
      this.view = 'trainings'
    }

    if (this.$route.query.from) {
      this.dateRange = {
        from: this.$route.query.from,
        to: this.$route.query.to
      }

      this.$router.replace({ query: {} })
    }

    this.fetchVariables()
    this.fetchPlayerDetails()
    this.fetchPlayerStats()
  },
  methods: {
    async fetchPlayerDetails() {
      try {
        this.player.loading = true

        const resp = await PlayerApiService.getPlayerDetails(this.$route.params.id)
        this.player.data = resp.data

        for (const zone of Object.keys(this.player.data.speed_profile)) {
          this.player.data.speed_profile[zone].lower = (this.player.data.speed_profile[zone].lower * 3.6).toFixed(1)
          this.player.data.speed_profile[zone].upper = (this.player.data.speed_profile[zone].upper * 3.6).toFixed(1)
        }
      } catch (e) {
        // todo
      } finally {
        this.player.loading = false
        await this.fetchTeamStats()
      }
    },
    async fetchPlayerStats() {
      try {
        this.playerStats.loading = true

        const queryParams = {
          page: this.playerStats.pagination.current_page,
          per_page: this.playerStats.pagination.per_page
        }

        if (this.dateRange) {
          queryParams.dateTo = dayjs(this.dateRange.to).format('YYYY-MM-DD')
          queryParams.dateFrom = dayjs(this.dateRange.from).format('YYYY-MM-DD')
        }

        const resp = await PlayerApiService.getStats(this.$route.params.id, queryParams)

        this.playerStats.maxValues = processMaxValues(resp.data.map(stat => stat.stats))

        this.playerStats.data = resp.data.map(stat => {
          if (!stat.stats) {
            return stat
          }

          if (stat.stats.SLD) stat.stats.SLD = (stat.stats.SLD / 60).toFixed(2)

          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
        this.playerStats.pagination = resp.meta

        this.calculateMenstrualAnnotations()
      } catch (e) {
        // todo
      } finally {
        this.playerStats.loading = false
      }
    },
    async fetchTeamStats() {
      if (!this.player.data) return

      this.teamStats.loading = true

      try {
        const teamStats = await TeamApiService.getStats(this.player.data.team_id, dayjs(this.date).toISOString(), dayjs(this.date).toISOString())

        this.teamStats.data = teamStats.map(stat => {
          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: this.$t('alert.pulling_team_stats.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teamStats.loading = false
      }
    },
    async updatePlayerPerformanceProfile(data) {
      try {
        this.saving = true

        await PlayerApiService.updatePerformanceProfile(this.player.data.id, {
          hr_profile: data.hr_profile,
          speed_profile: data.speed_profile,
          acceleration_profile: data.acceleration_profile
        })

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.performance_profile_update.success'),
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.performance_profile_update.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.saving = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        this.variables.data = await CoachApiService.getVariables(this.user.coach.id, {
          dashboard: this.dashboard
        })

        Object.keys(this.variables.data).forEach(group => {
          Object.keys(this.variables.data[group]).forEach(category => {
            this.variables.data[group][category] = this.variables.data[group][category].map(v => {
              return {
                ...v,
                text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
                long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
              }
            })
          })
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    calculateMenstrualAnnotations() {
      const phases = []

      const allMenstrualPhases = cloneDeep(MenstrualPhases)

      for (const data of this.playerStats.data) {
        if (!data.stats.cycle_phase || data.stats.cycle_phase === MenstrualPhase.NO_PHASE) continue

        const currentPhase = phases.find(p => p.key === data.stats.cycle_phase)

        if (!currentPhase) {
          const phaseObject = allMenstrualPhases[data.stats.cycle_phase]

          phaseObject.key = data.stats.cycle_phase
          phaseObject.startDate = data.date
          phaseObject.endDate = data.date

          phases.push(phaseObject)
        } else {
          currentPhase.startDate = data.date
        }
      }

      this.xAxisMenstrualAnnotations = phases.map(phase => {
        return {
          x: dayjs(phase.startDate).valueOf(),
          x2: dayjs(phase.endDate).add(1, 'day').valueOf(),
          opacity: 0.3,
          borderColor: phase.color,
          fillColor: phase.color,
          label: {
            borderColor: phase.color + '80', // hex transparency
            style: {
              color: '#fff',
              background: phase.color + '80' // hex transparency
            },
            text: phase.text,
            opacity: 0.5
          }
        }
      })

     // [ { x: n, x2: 1.3, opacity: 0.5, borderColor: '#86CC8E', fillColor: '#86CC8E' }, ]
    }

  }
}
</script>
